import type { PdfTemplateDesign } from "@workspace/api-zod/pdf-template";
import { hexRgb, tintRgb } from "./pdf-template-drawing";
type RGB = [number, number, number];

function luminance(rgb: RGB) {
  const c = rgb.map(v => { const n = v / 255; return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4; });
  return c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722;
}
export function brandContrast(foreground: RGB, background: RGB) {
  const [lo, hi] = [luminance(foreground), luminance(background)].sort((a, b) => a - b);
  return (hi + 0.05) / (lo + 0.05);
}
/** Darken along the selected hue, not to an unrelated fixed navy/teal. */
export function readableMainColour(hex: string): RGB {
  const original = hexRgb(hex);
  // Any badge tint is at least 232 in each channel, including the tint
  // generated from the darkened icon colour rather than the original colour.
  const backgrounds: RGB[] = [[255, 255, 255], [243, 243, 245], [232, 232, 232], tintRgb(hex)];
  const readable = (rgb: RGB) => backgrounds.every(bg => brandContrast(rgb, bg) >= 4.5);
  if (readable(original)) return original;
  let lo = 0, hi = 1;
  for (let i = 0; i < 16; i++) {
    const factor = (lo + hi) / 2;
    const rgb = original.map(v => Math.round(v * factor)) as RGB;
    if (readable(rgb)) lo = factor; else hi = factor;
  }
  return original.map(v => Math.round(v * lo)) as RGB;
}
export function rgbHex(rgb: RGB) { return `#${rgb.map(v => v.toString(16).padStart(2, "0")).join("")}`; }
export function pdfInteriorColour(design: PdfTemplateDesign | null): string | undefined {
  if (!design?.useInteriorBranding) return undefined;
  if ((design.pageVersion ?? 0) >= 4) return design.primaryColor;
  return design.pageVersion === 3 ? rgbHex(readableMainColour(design.primaryColor)) : design.accentColor;
}

/** Symbols need their own contrast even when the chosen interior colour is white. */
export function pdfIconColour(design: PdfTemplateDesign | null): string | undefined {
  const colour = pdfInteriorColour(design);
  return colour ? rgbHex(readableMainColour(colour)) : undefined;
}
